import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Btn, ConfirmModal, Heading, Modal, Spinner, ErrorState, StatusBadge } from '../../components/platform/ui';
import useAsync from '../../hooks/useAsync';
import { platformAdminApi, errorText } from '../../services/platformApi';
import { ImageField, Text, Area, Select, Check, lines, pairs, unpairs, DataTable, EnTranslations, initEn, buildTranslations } from './shared';

const EN_PRODUCT = [
  { key: 'name', label: 'Name', kind: 'text' },
  { key: 'short_description', label: 'Short description', kind: 'area', rows: 2 },
  { key: 'full_description', label: 'Full description', kind: 'area', rows: 6 },
  { key: 'features', label: 'Features', kind: 'lines', rows: 4 },
  { key: 'workflow', label: 'How it works', kind: 'pairs', a: 'title', b: 'text', rows: 4 },
  { key: 'faq', label: 'FAQ', kind: 'pairs', a: 'q', b: 'a', rows: 4 },
  { key: 'price_text', label: 'Price text', kind: 'text' },
  { key: 'cta_label', label: 'Button text', kind: 'text' },
];
const EN_TARIFF = [
  { key: 'name', label: 'Name', kind: 'text' },
  { key: 'description', label: 'Description', kind: 'area', rows: 2 },
];

const emptyProduct = {
  slug: '', name: '', short_description: '', full_description: '', cover_url: '', gallery: [], features: [], workflow: [], faq: [],
  product_type: 'web_app', launch_type: 'browser', launch_url: '', access_policy: 'contact', access_flag: '',
  price_text: '', cta_label: 'Подробнее', is_published: false, sort_order: 100,
};

const ProductForm = ({ initial, onSaved, onClose }) => {
  const { t } = useTranslation();
  const [f, setF] = useState(() => ({
    ...emptyProduct, ...initial, cover_url: initial?.cover_url || '', launch_url: initial?.launch_url || '', access_flag: initial?.access_flag || '',
    _features: (initial?.features || []).join('\n'),
    _gallery: unpairs(initial?.gallery, 'url', 'caption'),
    _workflow: unpairs(initial?.workflow, 'title', 'text'),
    _faq: unpairs(initial?.faq, 'q', 'a'),
  }));
  const [en, setEn] = useState(() => initEn(EN_PRODUCT, initial?.translations));
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const isNew = !initial?.id;

  const save = async () => {
    const body = {
      slug: f.slug, name: f.name, short_description: f.short_description, full_description: f.full_description,
      cover_url: f.cover_url || null,
      gallery: pairs(f._gallery, 'url', 'caption').map((g) => ({ url: g.url || null, caption: g.caption })),
      features: lines(f._features),
      workflow: pairs(f._workflow, 'title', 'text'),
      faq: pairs(f._faq, 'q', 'a'),
      product_type: f.product_type, launch_type: f.launch_type, launch_url: f.launch_url || null,
      access_policy: f.access_policy, access_flag: f.access_policy === 'user_flag' ? (f.access_flag || 'petrovich_access') : null,
      price_text: f.price_text, cta_label: f.cta_label, is_published: f.is_published, sort_order: Number(f.sort_order) || 0,
      translations: buildTranslations(EN_PRODUCT, en),
    };
    setBusy(true);
    try {
      isNew ? await platformAdminApi.createProduct(body) : await platformAdminApi.updateProduct(initial.id, body);
      toast.success(t('pl.admin.prod.saved'));
      onSaved();
    } catch (e) { toast.error(errorText(e, t('pl.admin.prod.saveFail'))); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? t('pl.admin.prod.titleNew') : t('pl.admin.prod.titleEdit', { name: initial.name })}
      footer={(<><Btn variant="secondary" onClick={onClose}>{t('pl.common.cancel')}</Btn><Btn onClick={save} loading={busy}>{t('pl.common.save')}</Btn></>)}>
      <Text label={t('pl.admin.prod.slug')} value={f.slug} onChange={set('slug')} hint={t('pl.admin.prod.slugHint')} disabled={!isNew} />
      <Text label={t('pl.admin.prod.name')} value={f.name} onChange={set('name')} />
      <Area label={t('pl.admin.prod.short')} rows={2} value={f.short_description} onChange={set('short_description')} />
      <Area label={t('pl.admin.prod.full')} rows={6} value={f.full_description} onChange={set('full_description')} hint={t('pl.admin.prod.fullHint')} />
      <ImageField label={t('pl.admin.prod.cover')} value={f.cover_url} onChange={set('cover_url')} />
      <Area label={t('pl.admin.prod.features')} rows={4} value={f._features} onChange={set('_features')} hint={t('pl.admin.prod.featuresHint')} />
      <Area label={t('pl.admin.prod.workflow')} rows={4} value={f._workflow} onChange={set('_workflow')} hint={t('pl.admin.prod.workflowHint')} />
      <Area label={t('pl.admin.prod.gallery')} rows={3} value={f._gallery} onChange={set('_gallery')} hint={t('pl.admin.prod.galleryHint')} />
      <Area label={t('pl.admin.prod.faq')} rows={4} value={f._faq} onChange={set('_faq')} hint={t('pl.admin.prod.faqHint')} />
      <Select label={t('pl.admin.prod.type')} value={f.product_type} onChange={set('product_type')} options={[['web_app', t('pl.admin.prod.typeWeb')], ['desktop_app', t('pl.admin.prod.typeDesktop')], ['digital_material', t('pl.admin.prod.typeMaterial')], ['custom_service', t('pl.admin.prod.typeCustom')]]} />
      <Select label={t('pl.admin.prod.launch')} value={f.launch_type} onChange={set('launch_type')} options={[['browser', t('pl.admin.prod.launchBrowser')], ['download', t('pl.admin.prod.launchDownload')], ['contact', t('pl.admin.prod.launchContact')]]} />
      <Text label={t('pl.admin.prod.launchUrl')} value={f.launch_url} onChange={set('launch_url')} hint={t('pl.admin.prod.launchUrlHint')} />
      <Select label={t('pl.admin.prod.policy')} value={f.access_policy} onChange={set('access_policy')} options={[['free', t('pl.admin.prod.policyFree')], ['registered', t('pl.admin.prod.policyRegistered')], ['usage_tokens', t('pl.admin.prod.policyTokens')], ['user_flag', t('pl.admin.prod.policyFlag')], ['contact', t('pl.admin.prod.policyContact')], ['admin', t('pl.admin.prod.policyAdmin')]]} />
      {f.access_policy === 'user_flag' && <Select label={t('pl.admin.prod.flag')} value={f.access_flag || 'petrovich_access'} onChange={set('access_flag')} options={[['petrovich_access', t('pl.admin.prod.flagPetro')]]} />}
      <Text label={t('pl.admin.prod.priceText')} value={f.price_text} onChange={set('price_text')} hint={t('pl.admin.prod.priceTextHint')} />
      <Text label={t('pl.admin.prod.cta')} value={f.cta_label} onChange={set('cta_label')} />
      <Text label={t('pl.admin.prod.order')} type="number" value={f.sort_order} onChange={set('sort_order')} />
      <Check label={t('pl.admin.prod.published')} value={f.is_published} onChange={set('is_published')} />
      <EnTranslations spec={EN_PRODUCT} value={en} onChange={setEn} />
    </Modal>
  );
};

const TariffForm = ({ initial, onSaved, onClose }) => {
  const { t } = useTranslation();
  const [f, setF] = useState({ slug: '', name: '', description: '', price: '', tokens_count: 1, product_slug: 'cbct-segmentation', is_active: true, sort_order: 100, ...initial });
  const [en, setEn] = useState(() => initEn(EN_TARIFF, initial?.translations));
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const isNew = !initial?.id;
  const save = async () => {
    const body = { slug: f.slug, name: f.name, description: f.description || null, price: Number(f.price), tokens_count: Number(f.tokens_count),
      product_slug: f.product_slug || null, is_active: f.is_active, sort_order: Number(f.sort_order) || 0, translations: buildTranslations(EN_TARIFF, en) };
    setBusy(true);
    try { isNew ? await platformAdminApi.createTariff(body) : await platformAdminApi.updateTariff(initial.id, body); toast.success(t('pl.admin.prod.tariffSaved')); onSaved(); }
    catch (e) { toast.error(errorText(e, t('pl.admin.prod.saveFail'))); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={isNew ? t('pl.admin.prod.tariffNew') : t('pl.admin.prod.tariffEdit', { name: initial.name })}
      footer={(<><Btn variant="secondary" onClick={onClose}>{t('pl.common.cancel')}</Btn><Btn onClick={save} loading={busy}>{t('pl.common.save')}</Btn></>)}>
      <Text label="Slug" value={f.slug} onChange={set('slug')} disabled={!isNew} />
      <Text label={t('pl.admin.prod.name')} value={f.name} onChange={set('name')} />
      <Area label={t('pl.admin.prod.description')} rows={2} value={f.description} onChange={set('description')} />
      <Text label={t('pl.admin.prod.price')} type="number" min="1" step="0.01" value={f.price} onChange={set('price')} hint={t('pl.admin.prod.priceHint')} />
      <Text label={t('pl.admin.prod.tokensCount')} type="number" min="1" value={f.tokens_count} onChange={set('tokens_count')} />
      <Text label={t('pl.admin.prod.productSlug')} value={f.product_slug} onChange={set('product_slug')} />
      <Text label={t('pl.admin.prod.order2')} type="number" value={f.sort_order} onChange={set('sort_order')} />
      <Check label={t('pl.admin.prod.active')} value={f.is_active} onChange={set('is_active')} />
      <EnTranslations spec={EN_TARIFF} value={en} onChange={setEn} />
    </Modal>
  );
};

const AdminProducts = () => {
  const { t } = useTranslation();
  const prods = useAsync(platformAdminApi.products, []);
  const tariffs = useAsync(platformAdminApi.tariffs, []);
  const [editP, setEditP] = useState(null);
  const [editT, setEditT] = useState(null);
  const [delP, setDelP] = useState(null);
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    setBusy(true);
    try { await platformAdminApi.deleteProduct(delP.id); toast.success(t('pl.admin.prod.deleted')); setDelP(null); prods.reload(); }
    catch (e) { toast.error(errorText(e, t('pl.admin.prod.deleteFail'))); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-10">
      <section>
        <div className="flex justify-between items-center mb-3"><Heading level={2} className="text-2xl">{t('pl.admin.prod.sectionProducts')}</Heading><Btn onClick={() => setEditP({})}>{t('pl.admin.prod.addProduct')}</Btn></div>
        {prods.loading ? <Spinner /> : prods.error ? <ErrorState onRetry={prods.reload} /> : (
          <DataTable rows={prods.data} columns={[
            { label: t('pl.admin.prod.colName'), render: (p) => <b>{p.name}</b> },
            { label: 'Slug', key: 'slug' },
            { label: t('pl.admin.prod.colAccess'), key: 'access_policy' },
            { label: t('pl.admin.prod.colStatus'), render: (p) => p.is_published ? <StatusBadge tone="green">{t('pl.admin.prod.statusPublished')}</StatusBadge> : <StatusBadge>{t('pl.admin.prod.statusDraft')}</StatusBadge> },
            { label: '', render: (p) => (<div className="flex gap-2"><Btn variant="secondary" onClick={() => setEditP(p)}>{t('pl.common.edit')}</Btn><Btn variant="secondary" onClick={() => setDelP(p)}>{t('pl.common.delete')}</Btn></div>) },
          ]} />
        )}
      </section>
      <section>
        <div className="flex justify-between items-center mb-3"><Heading level={2} className="text-2xl">{t('pl.admin.prod.sectionTariffs')}</Heading><Btn onClick={() => setEditT({})}>{t('pl.admin.prod.addTariff')}</Btn></div>
        <p className="text-sm text-gray-600 mb-3">{t('pl.admin.prod.tariffNote')}</p>
        {tariffs.loading ? <Spinner /> : tariffs.error ? <ErrorState onRetry={tariffs.reload} /> : (
          <DataTable rows={tariffs.data} columns={[
            { label: t('pl.admin.prod.colName'), render: (tf) => <b>{tf.name}</b> },
            { label: t('pl.admin.prod.colPrice'), render: (tf) => `${tf.price} ₽` },
            { label: t('pl.admin.prod.colSegmentations'), key: 'tokens_count' },
            { label: t('pl.admin.prod.colStatus'), render: (tf) => tf.is_active ? <StatusBadge tone="green">{t('pl.admin.prod.tariffActive')}</StatusBadge> : <StatusBadge>{t('pl.admin.prod.tariffOff')}</StatusBadge> },
            { label: '', render: (tf) => <Btn variant="secondary" onClick={() => setEditT(tf)}>{t('pl.common.edit')}</Btn> },
          ]} />
        )}
      </section>
      {editP && <ProductForm initial={editP.id ? editP : null} onClose={() => setEditP(null)} onSaved={() => { setEditP(null); prods.reload(); }} />}
      {editT && <TariffForm initial={editT.id ? editT : null} onClose={() => setEditT(null)} onSaved={() => { setEditT(null); tariffs.reload(); }} />}
      <ConfirmModal open={!!delP} busy={busy} onClose={() => setDelP(null)} onConfirm={remove} confirmLabel={t('pl.common.delete')}
        text={t('pl.admin.prod.deleteConfirm', { name: delP?.name })} />
    </div>
  );
};

export default AdminProducts;
